import L from "leaflet";
import mustache from "mustache";
import {
  getDescargaLitrosHa,
  getMezclaLitros,
  LoteMedido,
  ParametrosMedicion,
} from "../../../../modulos/aplicaciones/calculoAplicacion";
import { AplicacionResponse } from "../../../../modulos/aplicaciones/response/AplicacionResponse";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import {
  BaseMapRender,
  PopupConfig,
  PopupItem,
} from "../../../base/map/BaseMapRender";
import { ConfigTopActions } from "../../../implementations/map/TopActions";
import popupTemplate from "../../../base/map/popupTemplate.html";

mustache.parse(popupTemplate);

/** Estilo de la trayectoria de vuelo. */
const VUELO_STYLE: L.PathOptions = {
  color: "#2563eb",
  weight: 3,
  opacity: 0.8,
};

/** Estilo del lote **aplicado** en una aplicación manual: relleno azul, el
 *  mismo color con el que se dibuja el vuelo del dron. Son la misma pregunta
 *  —dónde se aplicó— contestada con lo que cada método sabe. */
const LOTE_APLICADO_STYLE: L.PathOptions = {
  fillColor: "#2563eb",
  fillOpacity: 0.45,
  color: "#2563eb",
  weight: 2.5,
  opacity: 1,
};

/** Formatea con separador de miles y sin forzar decimales. */
const fx_num = (valor: number, decimales: number = 1): string =>
  valor.toLocaleString("es-CO", { maximumFractionDigits: decimales });

/**
 * Mapa del módulo de aplicaciones: los lotes de la finca como base y, encima,
 * **dónde se aplicó** la aplicación seleccionada. Cómo se muestra eso depende
 * del método:
 *
 * - **Dron**: la trayectoria del vuelo (`MultiLineString`) sobreimpresa en azul.
 * - **Manuales**: no hay trayectoria, así que se **rellenan los lotes
 *   aplicados** con ese mismo azul, y su popup suma lo que se les aplicó. Sin
 *   esto el mapa quedaba vacío en toda aplicación manual —`L.geoJSON(null)` no
 *   falla, simplemente no dibuja nada— y la pantalla parecía rota.
 *
 * Una sola instancia viva: el sidebar cambia la aplicación mostrada vía
 * `setAplicacion` (si el mapa aún no terminó de inicializar, queda pendiente y
 * se dibuja al estar listo). Ortomosaico sí, conteo no.
 */
export class AplicacionesMapRender extends BaseMapRender<AplicacionResponse> {
  constructor() {
    super();
    // El mapa Leaflet se crea async (connect): capturarlo y dibujar lo pendiente.
    this.setOnMapInitializeEnd((map) => {
      this._map = map;
      this._drawAplicacion();
    });
  }

  /** Muestra la aplicación elegida (la llama el sidebar al seleccionar). */
  setAplicacion(aplicacion: AplicacionResponse): void {
    this._aplicacion = aplicacion;
    this._drawAplicacion();
  }

  /** Sin datos extra: los lotes de la base bastan; la aplicación llega por
   *  `setAplicacion`. */
  protected doInitData(): Promise<AplicacionResponse[]> {
    return Promise.resolve([]);
  }

  /** El lote lleva la aplicación seleccionada **solo si fue uno de los
   *  aplicados**. Es lo que `getStyle` y el popup usan para distinguirlo del
   *  resto de la finca. */
  protected doGetResponse(
    lote: ClienteFincaLoteResponse,
    _responseList: AplicacionResponse[],
  ): AplicacionResponse | null {
    const aplicado = this._aplicacion?.lotes.some(
      (aplicado) => aplicado.loteId === lote.id,
    );
    return aplicado ? this._aplicacion : null;
  }

  /** Limpieza: remueve la capa de vuelo. */
  protected doDispose(): void {
    this._vueloLayer?.remove();
    this._vueloLayer = null;
  }

  /** Relleno azul en los lotes aplicados; el resto, el estilo de la base. */
  protected getStyle(
    lote: ClienteFincaLoteResponse,
    response: AplicacionResponse | null,
  ): L.PathOptions {
    if (response) return LOTE_APLICADO_STYLE;
    return super.getStyle(lote, response);
  }

  /** Tooltip default; ortomosaico sí, conteo no. */
  protected getConfigTopActions(): ConfigTopActions<AplicacionResponse> {
    return {
      toolTipItems: this.getDefaultTooltipItems(),
      withOrtomosaico: true,
      withConteo: false,
    };
  }

  /** Popup con los items default del lote y, si se le aplicó, cuánto. Sin
   *  edición (no es el mapa de configuración de lotes). */
  protected getPopupConfig(): PopupConfig<AplicacionResponse> | null {
    return {
      bindItems: (lote, response) => this._popupItems(lote, response),
      withEditLote: false,
    };
  }

  /** Instancia Leaflet (disponible al terminar la inicialización). */
  private _map: L.Map | null = null;
  /** Capa GeoJSON del vuelo actualmente dibujada (o null). */
  private _vueloLayer: L.GeoJSON | null = null;
  /** Aplicación seleccionada (pendiente de dibujar si el mapa no está listo). */
  private _aplicacion: AplicacionResponse | null = null;

  /** Dibuja la aplicación seleccionada: la trayectoria si es por dron, y el
   *  relleno de los lotes aplicados si es manual. */
  private _drawAplicacion(): void {
    if (!this._map || !this._aplicacion) return;

    this._vueloLayer?.remove();
    this._vueloLayer = null;

    if (this._aplicacion.vuelo) {
      this._vueloLayer = L.geoJSON(this._aplicacion.vuelo, {
        style: VUELO_STYLE,
      }).addTo(this._map);
    }

    this._refreshLotes();
  }

  /** Re-estiliza los polígonos y refresca sus popups. Las capas ya existen
   *  (`BaseMapRender` las arma una vez): acá solo cambian de color, igual que
   *  en el mapa de la vista de sesión de monitoreo. */
  private _refreshLotes(): void {
    this._groupList.forEach((group) => {
      const response = this.doGetResponse(group.lote, []);
      group.response = response;
      group.layer.setStyle(this.getStyle(group.lote, response));
      // `setContent` refresca solo si el popup está abierto; no hace falta
      // re-bindear (eso reintroduciría el click interno de Leaflet).
      group.popup?.setContent(this._popupContent(group.lote, response));
    });
  }

  /** Items del popup: los del lote y, si se le aplicó, cuánto. */
  private _popupItems(
    lote: ClienteFincaLoteResponse,
    response: AplicacionResponse | null,
  ): PopupItem[] {
    const items = this.getDefaultPopupItems(lote);
    const aplicado = response?.lotes.find(
      (candidato) => candidato.loteId === lote.id,
    );
    if (!response || !aplicado) return items;

    const parametros: ParametrosMedicion = {
      metodo: response.metodo,
      // Los litros **congelados** de la aplicación, no los que el recipiente
      // tenga hoy: corregirlo después no puede cambiar lo ya registrado.
      litrosRecipiente: response.volumenRecipienteLitros ?? 0,
      mezclaPorPlantaMl: response.mezclaPorPlantaMl ?? 0,
    };
    const medido: LoteMedido = {
      loteId: aplicado.loteId,
      loteNombre: aplicado.loteNombre,
      areaHectareas: lote.areaHectareas,
      cantidad: aplicado.cantidad,
      plantas: aplicado.plantas,
    };

    if (response.metodo === "drench") {
      items.push({
        label: "Plantas tratadas",
        content: fx_num(aplicado.plantas ?? 0, 0),
      });
    } else {
      items.push({
        label: response.recipienteNombre ?? "Recipientes",
        content: fx_num(aplicado.cantidad ?? 0),
      });
    }
    items.push({
      label: "Mezcla",
      content: `${fx_num(getMezclaLitros(medido, parametros))} L`,
    });
    items.push({
      label: "Descarga",
      content: `${fx_num(getDescargaLitrosHa(medido, parametros), 0)} L/ha`,
    });
    return items;
  }

  /** Mismo contenido que `bindItems`, ya como HTML del popup.
   *
   *  Hace falta aparte porque `setContent` recibe el HTML armado, no los items.
   *  Usa **la misma plantilla que `BaseMapRender`** y no una propia: el popup
   *  lleva el nombre del lote como encabezado y sus clases de estilo, y
   *  rearmarlo a mano quedaria distinto del que pinta la base al montar. */
  private _popupContent(
    lote: ClienteFincaLoteResponse,
    response: AplicacionResponse | null,
  ): string {
    return mustache.render(popupTemplate, {
      lote: lote.nombreLote,
      items: this._popupItems(lote, response),
      withEdit: false,
      loteId: lote.id,
    });
  }
}
